import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

const ToastContext = createContext(null);

const ICONS = {
  success: 'bi-check-circle-fill',
  danger: 'bi-exclamation-triangle-fill',
  warning: 'bi-exclamation-circle-fill',
  info: 'bi-info-circle-fill',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, variant = 'success') => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((list) => [...list, { id, message, variant }]);
  }, []);

  const api = useMemo(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(typeof m === 'string' ? m : m?.message || 'Something went wrong', 'danger'),
      info: (m) => push(m, 'info'),
      warn: (m) => push(m, 'warning'),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer position="top-end" className="p-3 app-toasts">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            bg={t.variant}
            onClose={() => dismiss(t.id)}
            delay={t.variant === 'danger' ? 6000 : 3500}
            autohide
          >
            <Toast.Body className="text-white d-flex align-items-center gap-2">
              <i className={`bi ${ICONS[t.variant]}`} />
              <span className="flex-grow-1">{t.message}</span>
              <button
                type="button"
                className="btn-close btn-close-white btn-sm"
                onClick={() => dismiss(t.id)}
                aria-label="Close"
              />
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
